import React from "react";

import { useIntl, FormattedMessage } from "react-intl";

import { LinkButton } from "@atlaskit/button-next/new";
import Heading from "@atlaskit/heading";
import ArrowUpRightIcon from "@atlaskit/icon/core/arrow-up-right";
import { Inline, Stack, Grid } from "@atlaskit/primitives-next";

import { GetL1AssessmentsResponse } from "@/libraries/bxp-web-stargate-client/src";

import { useFireAnalyticsEvents } from "../../../../../analytics";
import {
    SourceKey,
    ActionKey,
    ActionSubjectKey,
    ActionSubjectIdKey,
    SubjectAttributeKey
} from "../../../../../analytics/constants";
import { useFastshiftSelector } from "../../../../state";
import { messages } from "../messages";
import { SummaryCard } from "../summary-card";

export const MigrationSummary: React.FC = () => {
    const intl = useIntl();

    const { l1Assessments, migrationPlanTotals, instances, selectedOrg, orgs } =
        useFastshiftSelector((state) => ({
            l1Assessments: state.l1Assessments,
            migrationPlanTotals: state.migrationPlanTotals,
            instances: state.instances,
            selectedOrg: state.selectedOrg,
            orgs: state.orgs
        }));

    const isOrgAdmin = orgs.data.length > 1;

    const { fireTrackEvent, fireUIEvent } = useFireAnalyticsEvents();

    // send track event once the migration summary section is loaded
    const loadStartRef = React.useRef<number>(Date.now());
    const hasSentTrackEvent = React.useRef<boolean>(false);

    const isLoading =
        l1Assessments.pending ||
        migrationPlanTotals.pending ||
        instances.pending ||
        !selectedOrg;

    const baseUrl =
        process.env["NODE_ENV"] === "production"
            ? "https://admin.atlassian.com"
            : "https://admin.stg.atlassian.com";

    const migrationPlansUrl = `${baseUrl}/cloud-first-create-a-migration/${selectedOrg?.id}`;
    const assessmentsUrl = `${baseUrl}/o/${selectedOrg?.id}/product-insights/products`;

    const metrics = {
        areasToImprove:
            l1Assessments.data.filter(
                (assessment: GetL1AssessmentsResponse[number]) =>
                    assessment.consolidatedInsightsStatus === "HAS_ERRORS"
            ).length || 0,
        assessedProducts: l1Assessments.data.length || 0,
        apps: instances.data.totalCount || 0,
        plansCompleted: migrationPlanTotals.data.totalPlansCompleted || 0,
        plansUnsuccessful: migrationPlanTotals.data.totalPlansUnsuccessful || 0,
        plansRunning: migrationPlanTotals.data.totalPlansRunning || 0
    };

    React.useEffect(() => {
        const orgsLoaded =
            orgs && orgs.data && orgs.data.length > 0 && !orgs.pending;
        const selectedOrgLoaded = !!selectedOrg;

        if (orgsLoaded && selectedOrgLoaded && !hasSentTrackEvent.current) {
            const elapsed = Date.now() - loadStartRef.current;
            hasSentTrackEvent.current = true;

            fireTrackEvent({
                action: ActionKey.DISPLAYED,
                actionSubject: ActionSubjectKey.SECTION,
                actionSubjectId: ActionSubjectIdKey.MIGRATION_SUMMARY_SECTION,
                source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
                attributes: {
                    loadTime: elapsed,
                    isDisplayed: isOrgAdmin,
                    isEmptyBanner: false
                }
            });
        }
    }, [orgs, selectedOrg, isOrgAdmin, fireTrackEvent]);

    const handleLinkButtonClick = (subject: string) => {
        fireUIEvent({
            action: ActionKey.CLICKED,
            actionSubject: ActionSubjectKey.LINK,
            actionSubjectId: ActionSubjectIdKey.MIGRATION_SUMMARY_SECTION,
            source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
            attributes: {
                subject
            }
        });
    };

    return (
        <Stack space="space.200">
            <Inline spread="space-between" alignBlock="center">
                <Heading size="small">
                    <FormattedMessage {...messages.summaryTitle} />
                </Heading>
                <Inline as="div" space="space.100">
                    <LinkButton
                        href={assessmentsUrl}
                        iconAfter={ArrowUpRightIcon}
                        target="_blank"
                        onClick={() =>
                            handleLinkButtonClick(
                                SubjectAttributeKey.VIEW_ASSESSMENTS
                            )
                        }
                    >
                        {intl.formatMessage(messages.viewAssessments)}
                    </LinkButton>
                    <LinkButton
                        href={migrationPlansUrl}
                        iconAfter={ArrowUpRightIcon}
                        target="_blank"
                        onClick={() =>
                            handleLinkButtonClick(
                                SubjectAttributeKey.VIEW_MIGRATIONS
                            )
                        }
                    >
                        {intl.formatMessage(messages.viewMigrations)}
                    </LinkButton>
                </Inline>
            </Inline>
            <Grid
                rowGap="space.100"
                columnGap="space.100"
                templateColumns="1fr 1fr 1fr"
            >
                <SummaryCard
                    value={metrics.assessedProducts}
                    title={intl.formatMessage(
                        messages.dataCenterProductsAssessedTitle,
                        { assessments: metrics.assessedProducts }
                    )}
                    isLoading={isLoading}
                    hasError={l1Assessments.error !== null}
                />
                <SummaryCard
                    value={metrics.areasToImprove}
                    title={intl.formatMessage(messages.areasToImprove, {
                        assessments: metrics.areasToImprove
                    })}
                    isLoading={isLoading}
                    hasError={l1Assessments.error !== null}
                />
                <SummaryCard
                    value={metrics.apps}
                    title={intl.formatMessage(messages.detectedApps, {
                        apps: metrics.apps
                    })}
                    isLoading={isLoading}
                    hasError={instances.error !== null}
                />
                <SummaryCard
                    value={metrics.plansCompleted}
                    title={intl.formatMessage(
                        messages.migrationPlansSuccessfullyCompleted,
                        { plans: metrics.plansCompleted }
                    )}
                    valueColor="color.text.success"
                    isLoading={isLoading}
                    hasError={migrationPlanTotals.error !== null}
                />
                <SummaryCard
                    value={metrics.plansUnsuccessful}
                    title={intl.formatMessage(
                        messages.unsuccessfulMigrationPlans,
                        { plans: metrics.plansUnsuccessful }
                    )}
                    valueColor="color.text.warning"
                    isLoading={isLoading}
                    hasError={migrationPlanTotals.error !== null}
                />
                <SummaryCard
                    value={metrics.plansRunning}
                    title={intl.formatMessage(
                        messages.migrationsRunningCurrently,
                        { plans: metrics.plansRunning }
                    )}
                    valueColor="color.text.selected"
                    isLoading={isLoading}
                    hasError={migrationPlanTotals.error !== null}
                />
            </Grid>
        </Stack>
    );
};
